/**
 * Neo chat panel.
 *
 * POE owns page context, local tool steps, permissions, and the review-first
 * assistant surface. External Neo can still answer through the bridge, but the
 * panel remains useful when the bridge is not configured.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Send,
  Loader2,
  ChevronRight,
  Square,
  RotateCcw,
  Wrench,
  CheckCircle2,
  AlertCircle,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import { useNeoStatus, streamChat, type NeoStatus, type NeoStreamEvent } from '../../lib/neo.js';
import { api } from '../../lib/api.js';
import { Tooltip } from '../ui/tooltip.js';
import { cn } from '../../lib/utils.js';

interface Message {
  id: string;
  role: 'user' | 'neo';
  text: string;
  events?: NeoStreamEvent[];
  streaming?: boolean;
}

export interface NeoChatPanelProps {
  context?: Record<string, unknown>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: number;
}

const STORAGE_KEY = 'poe-neo-panel-open';

const QUICK_ACTIONS = [
  'Where am I?',
  'Summarize this deal',
  'Who submitted bids?',
  'Draft a portal reply',
];

const INBOX_QUICK_ACTIONS = [
  'Clean my inbox',
  'Scrub spam',
  'Draft follow-ups',
  'Summarize unread messages',
];

const AI_WORKFORCE_QUICK_ACTIONS = [
  'Review workforce health',
  'Show employee handoffs',
  'Find stuck automations',
  'Summarize recent AI work',
];

const DEAL_QUICK_ACTIONS = [
  'Summarize this deal',
  'Draft a follow-up',
  'List next steps',
  'Review deal risks',
];

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function contextLabel(context?: Record<string, unknown>) {
  const page = stringValue(context?.page) ?? stringValue(context?.source) ?? 'POE page';
  const route = stringValue(context?.route) ?? stringValue(context?.path);
  const dealId = stringValue(context?.dealId);
  const visible = Array.isArray(context?.visibleItemIds) ? context.visibleItemIds.length : 0;
  return {
    page,
    route,
    detail: dealId ? `Deal ${dealId}` : visible > 0 ? `${visible} visible items` : route ?? 'Page context loaded',
  };
}

function quickActionsForContext(context?: Record<string, unknown>) {
  const page = (stringValue(context?.page) ?? '').toLowerCase();
  const route = (stringValue(context?.route) ?? stringValue(context?.path) ?? '').toLowerCase();
  if (page === 'inbox' || route.startsWith('/inbox')) return INBOX_QUICK_ACTIONS;
  if (
    page === 'ai-workforce' ||
    page === 'ai-employees' ||
    page === 'voice-profiles' ||
    page === 'batches' ||
    route.startsWith('/ai-workforce') ||
    route.startsWith('/ai-employees') ||
    route.startsWith('/voice-profiles') ||
    route.startsWith('/batches')
  ) {
    return AI_WORKFORCE_QUICK_ACTIONS;
  }
  if (page === 'deal-detail' || Boolean(stringValue(context?.dealId)) || route.startsWith('/deals/')) return DEAL_QUICK_ACTIONS;
  return QUICK_ACTIONS;
}

function sourceLabel(source: string | undefined, live: boolean | undefined) {
  if (!source) return 'Checking Neo';
  if (source === 'live' && live) return 'Live Neo';
  if (source === 'poe') return 'POE fallback';
  if (source === 'stub') return 'Demo stubs';
  return 'Config needed';
}

function neoStatusCopy(status: NeoStatus | undefined) {
  if (!status) {
    return {
      label: 'Checking Neo',
      body: 'Confirming whether this session is using external Neo or the POE fallback.',
      tone: 'neutral' as const,
      showWiring: false,
    };
  }
  if (status.source === 'live' && status.live) {
    return {
      label: 'External Neo connected',
      body: 'This slider is using the live Neo bridge for chat, tool events, and learning context.',
      tone: 'ready' as const,
      showWiring: false,
    };
  }
  if (status.source === 'poe') {
    return {
      label: 'POE fallback active',
      body: 'This slider still uses POE page context and guarded tools. External Neo needs NEO_BASE_URL and NEO_BRIDGE_TOKEN in production.',
      tone: 'fallback' as const,
      showWiring: true,
    };
  }
  if (status.source === 'stub') {
    return {
      label: 'Local demo fallback',
      body: 'This looks like local fallback data. Production should use external Neo or POE-derived signals, not demo stubs.',
      tone: 'fallback' as const,
      showWiring: true,
    };
  }
  return {
    label: 'Neo offline',
    body: 'The live bridge is not available right now. Use Wiring to confirm secrets and provider reachability.',
    tone: 'setup' as const,
    showWiring: true,
  };
}

function stepLabel(event: NeoStreamEvent) {
  if (event.type === 'status') return event.message;
  if (event.type === 'tool_start') return event.tool.label ?? event.tool.name;
  if (event.type === 'tool_result') return event.result.summary;
  if (event.type === 'error') return event.message;
  return '';
}

function stepIcon(event: NeoStreamEvent) {
  if (event.type === 'tool_start') return Wrench;
  if (event.type === 'tool_result') return CheckCircle2;
  if (event.type === 'error') return AlertCircle;
  return Sparkles;
}

function StepRow({ event }: { event: NeoStreamEvent }) {
  if (event.type === 'text' || event.type === 'done') return null;
  const Icon = stepIcon(event);
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[11px]',
        event.type === 'error'
          ? 'border-danger/30 bg-danger/5 text-danger'
          : 'border-line bg-surface text-ink-muted'
      )}
    >
      <Icon size={12} className="flex-shrink-0" />
      <span className="min-w-0 truncate">{stepLabel(event)}</span>
    </div>
  );
}

function NeoStatusStrip({ status }: { status: NeoStatus | undefined }) {
  const copy = neoStatusCopy(status);
  const toneClass = {
    ready: 'border-success/20 bg-success/10 text-success',
    fallback: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-700/40 dark:bg-amber-900/40 dark:text-amber-100',
    setup: 'border-danger/20 bg-danger/10 text-danger',
    neutral: 'border-line bg-surface text-ink-muted',
  }[copy.tone];

  return (
    <div className={`rounded-xl border px-3 py-2 text-xs ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold">{copy.label}</p>
          <p className="mt-0.5 leading-4 opacity-90">{copy.body}</p>
        </div>
        {copy.showWiring && (
          <a
            href="/admin/wiring"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-current/20 bg-surface/70 px-2 py-1 font-semibold text-current hover:bg-surface"
          >
            Wiring
            <ExternalLink size={10} aria-hidden />
          </a>
        )}
      </div>
    </div>
  );
}

export default function NeoChatPanel({ context, open: controlledOpen, onOpenChange, width = 360 }: NeoChatPanelProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback((v: boolean) => {
    setUncontrolledOpen(v);
    try { localStorage.setItem(STORAGE_KEY, v ? '1' : '0'); } catch { /* ignore */ }
    onOpenChange?.(v);
  }, [onOpenChange]);

  const contextInfo = useMemo(() => contextLabel(context), [context]);
  const quickActions = useMemo(() => quickActionsForContext(context), [context]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeControllerRef = useRef<AbortController | null>(null);
  const lastPromptRef = useRef('');

  const { data: status } = useNeoStatus();
  const statusLabel = sourceLabel(status?.source, status?.live);
  const canRetry = !streaming && lastPromptRef.current.length > 0;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        id: 'greet',
        role: 'neo',
        text: 'I can read the current POE page, inspect loaded deal messages and Site Prep bid cards, and draft actions for review. I will not send outbound messages from chat.',
        events: [{ type: 'status', message: `${statusLabel} assistant ready.` }],
      }]);
    }
  }, [messages.length, open, statusLabel]);

  const applyEvent = useCallback((message: Message, event: NeoStreamEvent): Message => {
    if (event.type === 'text') return { ...message, text: `${message.text}${event.text}` };
    if (event.type === 'done') return { ...message, streaming: false };
    return { ...message, events: [...(message.events ?? []), event] };
  }, []);

  // Reflect a completed Neo turn into the operator's standing Neo inbox thread
  // (native + resumable). The Neo run itself is unchanged; this is an additive,
  // best-effort side-write that never disrupts the slider.
  const persistNeoTurn = useCallback(async (userText: string, neoText: string, clientTurnId: string) => {
    try {
      await api.post('/api/ai-threads/neo/turn', { userText, neoText, clientTurnId });
    } catch {
      /* persistence is additive; swallow */
    }
  }, []);

  const handleSend = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', text };
    const neoMsg: Message = { id: `n-${Date.now()}`, role: 'neo', text: '', events: [], streaming: true };
    const controller = new AbortController();
    activeControllerRef.current = controller;
    lastPromptRef.current = text;

    setMessages((items) => [...items, userMsg, neoMsg]);
    setInput('');
    setStreaming(true);

    try {
      let neoText = '';
      for await (const event of streamChat(text, context ?? {}, controller.signal)) {
        if (event.type === 'done') break;
        if (event.type === 'text') neoText += event.text;
        setMessages((items) => items.map((msg) => (msg.id === neoMsg.id ? applyEvent(msg, event) : msg)));
      }
      // Persist the completed turn-pair into the operator's standing Neo inbox thread.
      if (neoText.trim()) void persistNeoTurn(text, neoText, neoMsg.id);
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === 'AbortError';
      setMessages((items) =>
        items.map((msg) =>
          msg.id === neoMsg.id
            ? applyEvent(msg, {
                type: aborted ? 'status' : 'error',
                message: aborted ? 'Stopped by user.' : 'Neo stream failed before completing.',
              })
            : msg
        )
      );
    } finally {
      setMessages((items) => items.map((msg) => (msg.id === neoMsg.id ? { ...msg, streaming: false } : msg)));
      setStreaming(false);
      activeControllerRef.current = null;
    }
  }, [applyEvent, context, input, streaming, persistNeoTurn]);

  const stopStreaming = useCallback(() => {
    activeControllerRef.current?.abort();
  }, []);

  const retryLast = useCallback(() => {
    if (lastPromptRef.current) void handleSend(lastPromptRef.current);
  }, [handleSend]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.aside
        key="neo-panel"
        initial={{ x: width + 24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: width + 24, opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        style={{ width, paddingTop: 'env(safe-area-inset-top, 0px)' }}
        className={cn(
          'fixed top-0 right-0 h-screen',
          'bg-surface-raised border-l border-line',
          'flex flex-col z-modal'
        )}
        role="complementary"
        aria-label="Neo assistant"
      >
        <header className="px-4 py-3 border-b border-line space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center">
              <Sparkles size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-ink">Neo</h2>
              <p className="text-[10px] text-ink-subtle leading-tight">{statusLabel}</p>
            </div>
            <Tooltip content="Collapse panel" side="bottom">
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Collapse Neo panel"
                className="p-1 rounded-md text-ink-muted hover:text-ink hover:bg-surface-sunken transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </Tooltip>
          </div>
          <div className="rounded-xl border border-line bg-surface px-3 py-2">
            <div className="flex items-center gap-2 text-xs font-medium text-ink">
              <MapPin size={12} className="text-primary" />
              <span className="truncate">{contextInfo.page}</span>
            </div>
            <p className="mt-0.5 truncate text-[10px] text-ink-subtle">{contextInfo.detail}</p>
          </div>
          <NeoStatusStrip status={status} />
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
                  message.role === 'user'
                    ? 'bg-primary text-primary-fg'
                    : 'bg-surface-sunken text-ink'
                )}
              >
                {message.role === 'neo' && message.events && message.events.length > 0 && (
                  <div className="mb-2 space-y-1.5">
                    {message.events.map((event, idx) => (
                      <StepRow key={`${message.id}-event-${idx}`} event={event} />
                    ))}
                  </div>
                )}
                {message.text && <span className="whitespace-pre-wrap">{message.text}</span>}
                {message.streaming && (
                  <span className="inline-flex items-center gap-1 ml-1 text-ink-muted align-middle">
                    <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                    <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse [animation-delay:120ms]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse [animation-delay:240ms]" />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <footer className="border-t border-line p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {quickActions.map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => void handleSend(action)}
                disabled={streaming}
                className="rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-muted hover:border-primary/40 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {action}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSend();
            }}
            className="flex items-end gap-2"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              placeholder="Ask Neo about this page..."
              disabled={streaming}
              rows={2}
              className="flex-1 resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
            />
            {streaming ? (
              <Tooltip content="Stop generation" side="top">
                <button
                  type="button"
                  onClick={stopStreaming}
                  aria-label="Stop Neo"
                  className="h-9 w-9 rounded-lg bg-surface-sunken text-ink hover:bg-danger/10 hover:text-danger transition-colors flex items-center justify-center flex-shrink-0"
                >
                  <Square size={14} />
                </button>
              </Tooltip>
            ) : (
              <Tooltip content="Send" shortcut="Enter" side="top">
                <button
                  type="submit"
                  disabled={!input.trim()}
                  aria-label="Send"
                  className="h-9 w-9 rounded-lg bg-primary text-primary-fg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center flex-shrink-0"
                >
                  <Send size={14} />
                </button>
              </Tooltip>
            )}
          </form>
          <div className="flex items-center justify-between text-[10px] text-ink-subtle">
            <span>{status?.source === 'live' ? 'External Neo is connected.' : 'Using POE-local context and guarded tools.'}</span>
            <button
              type="button"
              onClick={retryLast}
              disabled={!canRetry}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {canRetry ? <RotateCcw size={10} /> : <Loader2 size={10} className="opacity-0" />}
              Retry
            </button>
          </div>
        </footer>
      </motion.aside>
    </AnimatePresence>
  );
}
